// Choisir un chantier en tapant le nom du client, l'objet des travaux ou le
// numéro. Même champ que pour les clients ; seules changent les données.

import {
  type ChantierRecherchable,
  chercherChantiers,
} from '@imp/shared/recherche-chantier';
import { useCallback, useMemo } from 'react';

import { ChoixListe, type OptionChoix } from '@/components/ChoixListe';
import { CHANTIER_STATUT_LABELS, type ChantierListItem } from '@/modules/chantiers/api';
import { contactDisplayName, type Contact } from '@/modules/clients/api';

/** Le chantier tel que la recherche partagée l'attend. */
function versRecherchable(
  ch: ChantierListItem,
  clients: Map<string, Contact>,
): ChantierRecherchable {
  const c = ch.contact_id === null ? undefined : clients.get(ch.contact_id);
  return {
    id: ch.id,
    numero: ch.numero,
    libelle: ch.libelle,
    client: c ? contactDisplayName(c) : null,
    ville: ch.ville,
    statut: ch.statut,
  };
}

function option(c: ChantierRecherchable): OptionChoix {
  return {
    id: c.id,
    // Le client d'abord : c'est par lui qu'on reconnaît un chantier.
    nom: c.client === null ? c.libelle : `${c.client} — ${c.libelle}`,
    detail: [c.numero, c.ville, CHANTIER_STATUT_LABELS[c.statut] ?? c.statut]
      .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
      .join(' · '),
  };
}

interface ChoixChantierProps {
  id?: string;
  chantiers: readonly ChantierListItem[];
  /** Pour afficher le nom du client au lieu de son identifiant. */
  clients: readonly Contact[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /** Libellé de l'option « pas de chantier ». Absent : le choix est obligatoire. */
  labelAucun?: string;
  disabled?: boolean;
  chargement?: boolean;
  invalide?: boolean;
}

export function ChoixChantier({
  id,
  chantiers,
  clients,
  value,
  onChange,
  placeholder = 'Taper un client, un objet, un numéro…',
  labelAucun,
  disabled = false,
  chargement = false,
  invalide = false,
}: ChoixChantierProps) {
  const recherchables = useMemo(() => {
    const parId = new Map(clients.map((c) => [c.id, c]));
    return chantiers.map((ch) => versRecherchable(ch, parId));
  }, [chantiers, clients]);

  const filtrer = useCallback(
    (saisie: string): OptionChoix[] => chercherChantiers(recherchables, saisie).map(option),
    [recherchables],
  );

  const choisi = useMemo((): OptionChoix | null => {
    const c = recherchables.find((x) => x.id === value);
    return c ? option(c) : null;
  }, [recherchables, value]);

  return (
    <ChoixListe
      {...(id === undefined ? {} : { id })}
      filtrer={filtrer}
      choisi={choisi}
      value={value}
      onChange={onChange}
      nomObjet="chantier"
      nomPluriel="chantiers"
      placeholder={placeholder}
      {...(labelAucun === undefined ? {} : { labelAucun })}
      disabled={disabled}
      chargement={chargement}
      aucunEnregistre={chantiers.length === 0}
      invalide={invalide}
    />
  );
}
